'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';

interface ComparisonBarMobileProps {
  title: string;
  value: number;
  maxValue: number;
  unit?: string;
  comparison?: {
    value: number;
    label: string;
  };
  color?: string;
  showPercentage?: boolean;
  icon?: React.ReactNode;
}

const ComparisonBarMobile: React.FC<ComparisonBarMobileProps> = ({
  title,
  value,
  maxValue,
  unit = '',
  comparison,
  color = '#3B82F6',
  showPercentage = true,
  icon
}) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  const roundedPercentage = Math.round(percentage);
  
  const getTrendIcon = () => {
    if (!comparison) return null;
    
    const diff = value - comparison.value;
    const percentChange = comparison.value > 0 ? (diff / comparison.value) * 100 : 0;
    
    if (Math.abs(percentChange) < 1) {
      return <Minus className="w-4 h-4 text-gray-500" />;
    } else if (percentChange > 0) {
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    } else {
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    }
  };
  
  const getTrendText = () => {
    if (!comparison) return null;
    
    const diff = value - comparison.value;
    const percentChange = comparison.value > 0 ? (diff / comparison.value) * 100 : 0;
    const sign = diff > 0 ? '+' : '';
    
    return `${sign}${Math.round(percentChange)}%`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-1">
          {icon && (
            <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${color}20` }}>
              {React.cloneElement(icon as React.ReactElement, { 
                className: 'w-4 h-4',
                style: { color } 
              })}
            </div>
          )}
          <h4 className="text-sm font-medium text-gray-700 line-clamp-1">{title}</h4>
        </div>
        <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      </div>
      
      {/* Value and Percentage */}
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <span className="text-xl font-bold text-gray-900">
            {value.toLocaleString()}
          </span>
          {unit && <span className="text-sm text-gray-500 ml-1">{unit}</span>}
        </div>
        {showPercentage && (
          <span className="text-sm font-medium" style={{ color }}>
            {roundedPercentage}%
          </span>
        )}
      </div>
      
      {/* Progress Bar */}
      <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="absolute top-0 left-0 h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
      
      {/* Comparison */}
      {comparison && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">{comparison.label}</span>
          <div className="flex items-center gap-1">
            {getTrendIcon()}
            <span className={`font-medium ${
              value > comparison.value ? 'text-green-600' :
              value < comparison.value ? 'text-red-600' :
              'text-gray-600'
            }`}>
              {getTrendText()}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ComparisonBarMobile;